import React, { useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ChevronRight, CircleCheck, CircleX, X } from "lucide-react";
import Wordmark from "../Wordmark/Wordmark";
import { LANDING_PLAN_CLICK_ALL, track } from "./landingTrack";
import "./LandingSampleDialog.css";

export type LandingSampleDialogChoice = {
  id: string;
  text: string;
};

export type LandingSampleDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Server said the picked choice was the right one. */
  correct: boolean;
  /** Id of the choice the visitor picked. Only that row is marked. */
  pickedChoiceId: string;
  meta: string;
  statement: string;
  choices: readonly LandingSampleDialogChoice[];
};

export default function LandingSampleDialog({
  open,
  onClose,
  correct,
  pickedChoiceId,
  meta,
  statement,
  choices,
}: LandingSampleDialogProps) {
  const titleId = React.useId();
  const phraseId = React.useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // StrictMode runs the effect, cleans up, and runs it again. The cleanup
  // defers history.back(); the second run cancels that timer and keeps the
  // one entry that the first run already pushed.
  const pendingBackRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!open) return;

    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    const opener =
      previousFocus instanceof HTMLElement && dialog !== null && !dialog.contains(previousFocus)
        ? previousFocus
        : null;

    // Move focus out of #root before that node becomes inert.
    headingRef.current?.focus({ preventScroll: true });

    const wordmark = dialog?.querySelector(".lsd__bar a");
    if (wordmark instanceof HTMLAnchorElement) wordmark.tabIndex = -1;

    const root = document.getElementById("root");
    const rootLock = root ? lockPageRoot(root) : null;

    if (pendingBackRef.current !== null) {
      window.clearTimeout(pendingBackRef.current);
      pendingBackRef.current = null;
    } else if (!isDialogHistoryState(window.history.state)) {
      const previous: unknown = window.history.state;
      const base =
        previous !== null && typeof previous === "object"
          ? { ...(previous as Record<string, unknown>) }
          : {};
      // Push while the page is still at its real scroll offset. The entry
      // we leave keeps that offset for the browser Back control.
      window.history.pushState({ ...base, landingSampleDialog: true }, "");
    }

    const scrollLock = lockPageScroll();

    const onPop = (event: PopStateEvent) => {
      // The data router also listens for popstate. Letting it through starts
      // a navigation of `/` and can drop the scroll position we just restored.
      event.stopImmediatePropagation();
      onCloseRef.current();
    };
    window.addEventListener("popstate", onPop, true);

    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      onCloseRef.current();
    };
    window.addEventListener("keydown", onEscape, true);

    return () => {
      window.removeEventListener("popstate", onPop, true);
      window.removeEventListener("keydown", onEscape, true);
      unlockPageRoot(rootLock);
      unlockPageScroll(scrollLock);
      opener?.focus({ preventScroll: true });
      // Button and Escape close here. Browser Back already dropped the entry,
      // so the timer finds nothing to remove. This popstate is swallowed so
      // the router does not treat it as a navigation away from `/`.
      pendingBackRef.current = window.setTimeout(() => {
        pendingBackRef.current = null;
        if (!isDialogHistoryState(window.history.state)) return;
        const swallow = (event: PopStateEvent) => {
          event.stopImmediatePropagation();
          window.removeEventListener("popstate", swallow, true);
        };
        window.addEventListener("popstate", swallow, true);
        window.history.back();
      }, 0);
    };
  }, [open]);

  if (!open) return null;

  const dismiss = () => {
    onCloseRef.current();
  };

  const onScrimClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (!window.matchMedia("(min-width: 768px)").matches) return;
    onCloseRef.current();
  };

  const onDialogKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab" || event.altKey || event.ctrlKey || event.metaKey) return;
    const tabbables = tabbableInDialog(event.currentTarget);
    const first = tabbables[0];
    const last = tabbables[tabbables.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    // Fechar is before the heading, and the heading is not a tab stop.
    if (active === headingRef.current) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
      return;
    }
    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const phrase = `Você marcou a alternativa ${letterFromId(pickedChoiceId)}.`;
  const statusIcon = correct ? (
    <CircleCheck
      size={28}
      className="lsd__status-icon lsd__status-icon--right"
      aria-hidden="true"
    />
  ) : (
    <CircleX
      size={28}
      className="lsd__status-icon lsd__status-icon--wrong"
      aria-hidden="true"
    />
  );

  return createPortal(
    <div className="landing-v3 lsd-root" onClick={onScrimClick}>
      <div
        className="lsd"
        role="dialog"
        aria-modal={true}
        aria-labelledby={titleId}
        aria-describedby={phraseId}
        ref={dialogRef}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={onDialogKeyDown}
      >
        <div className="lsd__bar">
          <Wordmark width={190} variant="light" to="/" label="Acertei Questões" />
        </div>
        <button
          type="button"
          className="lsd__close"
          aria-label="Fechar e voltar para a página"
          onClick={dismiss}
        >
          <X size={24} aria-hidden="true" />
        </button>
        <div className="lsd__body" tabIndex={-1}>
          <header className="lsd__result">
            <p className="lsd__eyebrow">QUESTÃO DE EXEMPLO</p>
            <div className="lsd__heading-row">
              {statusIcon}
              <h2 id={titleId} tabIndex={-1} ref={headingRef} className="lsd__title">
                {correct ? "Você acertou" : "Não foi dessa vez"}
              </h2>
            </div>
            <p id={phraseId} className="lsd__phrase">
              {phrase}
            </p>
          </header>
          <div className="lsd__question" tabIndex={-1}>
            <p className="lsd__meta">{meta}</p>
            <p className="lsd__statement">{statement}</p>
            <ul className="lsd__choices">
              {choices.map((choice) => {
                const picked = choice.id === pickedChoiceId;
                const className = !picked
                  ? "lsd__choice"
                  : correct
                    ? "lsd__choice lsd__choice--right"
                    : "lsd__choice lsd__choice--wrong";
                return (
                  <li key={choice.id} className={className}>
                    <span className="lsd__choice-main">
                      <span className="lsd__letter">{letterFromId(choice.id)}</span>
                      <span className="lsd__choice-text">{choice.text}</span>
                    </span>
                    {picked && correct ? (
                      <span className="lsd__mark">
                        <CircleCheck size={14} aria-hidden="true" />
                        Resposta correta
                      </span>
                    ) : null}
                    {picked && !correct ? (
                      <span className="lsd__mark">
                        <X size={14} aria-hidden="true" />
                        Resposta incorreta
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
          <footer className="lsd__invite">
            <h3 className="lsd__invite-title">Crie sua conta para continuar treinando</h3>
            <p className="lsd__invite-line">
              Resolva mais questões do seu concurso e faça simulados.
            </p>
            <div className="lsd__actions">
              <Link
                replace
                to="/registro"
                className="lsd__primary"
                onClick={() => {
                  track("sample_cta_clicked", { target: "registro" });
                }}
              >
                Criar conta
              </Link>
              <Link replace to="/login" className="lsd__secondary">
                Já tenho conta
              </Link>
              <Link
                replace
                to="/planos"
                className="lsd__plans"
                onClick={() => {
                  track("landing_plan_clicked", { product_id: LANDING_PLAN_CLICK_ALL });
                }}
              >
                Ver planos <ChevronRight size={18} aria-hidden="true" />
              </Link>
            </div>
          </footer>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function letterFromId(id: string): string {
  return id.length === 1 ? id.toLocaleUpperCase("pt-BR") : id;
}

function tabbableInDialog(dialog: HTMLElement): HTMLElement[] {
  return Array.from(
    dialog.querySelectorAll<HTMLElement>("a[href], button:not([disabled])"),
  ).filter((node) => node.tabIndex !== -1 && !isHiddenFromTabOrder(node));
}

function isHiddenFromTabOrder(element: HTMLElement): boolean {
  let node: HTMLElement | null = element;
  while (node) {
    const style = window.getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden") return true;
    node = node.parentElement;
  }
  return false;
}

type RootLock = {
  element: HTMLElement;
  hadInert: boolean;
  inert: string | null;
  hadAriaHidden: boolean;
  ariaHidden: string | null;
};

function lockPageRoot(root: HTMLElement): RootLock {
  const lock: RootLock = {
    element: root,
    hadInert: root.hasAttribute("inert"),
    inert: root.getAttribute("inert"),
    hadAriaHidden: root.hasAttribute("aria-hidden"),
    ariaHidden: root.getAttribute("aria-hidden"),
  };
  root.setAttribute("inert", "");
  root.setAttribute("aria-hidden", "true");
  return lock;
}

function unlockPageRoot(lock: RootLock | null) {
  if (!lock) return;
  if (lock.hadInert) lock.element.setAttribute("inert", lock.inert ?? "");
  else lock.element.removeAttribute("inert");
  if (lock.hadAriaHidden) lock.element.setAttribute("aria-hidden", lock.ariaHidden ?? "");
  else lock.element.removeAttribute("aria-hidden");
}

type ScrollLock = {
  y: number;
  position: string;
  top: string;
  left: string;
  right: string;
  width: string;
  overflow: string;
};

function lockPageScroll(): ScrollLock {
  const y = window.scrollY;
  const { body, documentElement } = document;
  const lock: ScrollLock = {
    y,
    position: body.style.position,
    top: body.style.top,
    left: body.style.left,
    right: body.style.right,
    width: body.style.width,
    overflow: documentElement.style.overflow,
  };
  body.style.position = "fixed";
  body.style.top = `-${y}px`;
  body.style.left = "0";
  body.style.right = "0";
  body.style.width = "100%";
  documentElement.style.overflow = "hidden";
  return lock;
}

function unlockPageScroll(lock: ScrollLock) {
  const { body, documentElement } = document;
  body.style.position = lock.position;
  body.style.top = lock.top;
  body.style.left = lock.left;
  body.style.right = lock.right;
  body.style.width = lock.width;
  documentElement.style.overflow = lock.overflow;
  window.scrollTo(0, lock.y);
}

function isDialogHistoryState(state: unknown): boolean {
  return (
    state !== null &&
    typeof state === "object" &&
    (state as { landingSampleDialog?: unknown }).landingSampleDialog === true
  );
}
